(() => {
  if (window.psGift) return;
  const route = window.Shopify?.routes?.root || '/';
  const selector = '[data-ps-gift]';
  const fields = ['To', 'From', 'Gift message'];
  let variant, cart, busy = false, timer, refreshTimer, dirty = false;
  let selected = false, values = {}, queue = Promise.resolve(), revision = 0;
  const known = new WeakSet();
  const widgets = () => [...document.querySelectorAll(selector)];
  const status = (message = '', error = false) => widgets().forEach(el => {
    el.toggleAttribute('data-error', error);
    el.querySelector('[data-ps-gift-status]').textContent = message;
  });
  const request = async (path, body) => {
    const response = await fetch(route + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || 'Could not save gift card.');
    return result;
  };
  const isGift = item => Number(item.variant_id) === variant;
  const paint = () => widgets().forEach(el => {
    el.hidden = !cart?.items?.some(item => !isGift(item));
    const toggle = el.querySelector('[data-ps-gift-toggle]');
    toggle.checked = selected;
    toggle.disabled = busy;
    el.querySelector('[data-ps-gift-fields]').hidden = !selected;
    fields.forEach(name => {
      const field = el.querySelector(`[data-ps-gift-field="${name}"]`);
      if (document.activeElement !== field) field.value = values[name] || '';
    });
    el.querySelector('[data-ps-gift-count]').textContent = `${(values['Gift message'] || '').length}/240`;
  });
  const adopt = fresh => {
    cart = fresh;
    if (!dirty && !busy) {
      const item = cart.items.find(isGift);
      selected = !!item;
      values = Object.fromEntries(fields.map(name => [name, item?.properties?.[name] || '']));
    }
    paint();
  };
  const broadcast = fresh => {
    document.dispatchEvent(new CustomEvent('ps:cart:updated', { detail: { cart: fresh } }));
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
  };
  const persist = async () => {
    if (!dirty) return;
    const currentRevision = revision;
    const desired = selected;
    const properties = { _ps_gift_card: 'one_per_order', ...Object.fromEntries(fields.map(name => [name, values[name] || ''])) };
    busy = true;
    paint();
    status('Saving…');
    const interstitial = document.querySelector('[data-ps-checkout-interstitial]');
    if (interstitial) interstitial.dataset.psCartBusy = '1';
    document.dispatchEvent(new CustomEvent('shapes:cart:lock'));
    try {
      let fresh = await request('cart.js');
      const giftLines = fresh.items.filter(isGift);
      const keep = desired && fresh.items.some(item => !isGift(item));
      for (const item of giftLines.slice(keep ? 1 : 0)) {
        fresh = await request('cart/change.js', { id: item.key, quantity: 0 });
      }
      if (keep) {
        const item = fresh.items.find(isGift);
        if (item) fresh = await request('cart/change.js', { id: item.key, quantity: 1, properties });
        else {
          await request('cart/add.js', { items: [{ id: variant, quantity: 1, properties }] });
          fresh = await request('cart.js');
        }
      }
      if (currentRevision === revision) dirty = false;
      cart = fresh;
      if (!keep) selected = false;
      window.psGrowth?.track('gift_card_updated', { channel: 'cart', value: keep ? 4 : 0 });
      broadcast(fresh);
      status(keep ? 'Gift card and message saved ✓' : 'Gift card removed.');
    } catch (error) {
      status('We could not save your gift card. Please try again before checkout.', true);
      throw error;
    } finally {
      busy = false;
      if (interstitial) delete interstitial.dataset.psCartBusy;
      document.dispatchEvent(new CustomEvent('shapes:cart:unlock'));
      paint();
    }
  };
  const save = () => {
    clearTimeout(timer);
    queue = queue.catch(() => {}).then(persist);
    return queue;
  };
  const settle = async () => {
    await save();
    if (dirty) await save();
  };
  const refresh = async () => {
    if (!variant || busy || dirty) return;
    try {
      const fresh = await request('cart.js');
      adopt(fresh);
      const giftLines = fresh.items.filter(isGift);
      if (giftLines.length && (!fresh.items.some(item => !isGift(item)) || giftLines.length > 1 || giftLines[0].quantity !== 1)) {
        dirty = true;
        revision++;
        await save();
      }
    } catch (_) {}
  };
  const scheduleRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 180);
  };
  const mount = () => {
    let added = false;
    widgets().forEach(el => {
      if (known.has(el)) return;
      known.add(el);
      variant = Number(el.dataset.variant);
      added = true;
    });
    if (added) {
      paint();
      scheduleRefresh();
    }
  };
  document.addEventListener('change', event => {
    const el = event.target.closest(selector);
    if (!el || !event.target.matches('[data-ps-gift-toggle]')) return;
    selected = event.target.checked;
    dirty = true;
    revision++;
    paint();
    save().catch(() => {});
  });
  document.addEventListener('input', event => {
    const field = event.target.closest('[data-ps-gift-field]');
    if (!field) return;
    values[field.dataset.psGiftField] = field.value;
    dirty = true;
    revision++;
    paint();
    status('Saving…');
    clearTimeout(timer);
    timer = setTimeout(() => save().catch(() => {}), 650);
  });
  document.addEventListener('ps:cart:updated', event => {
    if (event.detail?.cart) adopt(event.detail.cart);
    scheduleRefresh();
  });
  ['theme:cart:change', 'theme:cart-drawer:open'].forEach(name => document.addEventListener(name, scheduleRefresh));
  ['shapes:cart:update', 'shapes:cart:cartqtychange', 'shapes:cart:afteradditem'].forEach(name => window.addEventListener(name, scheduleRefresh));
  document.addEventListener('ps:interstitial:opened', event => {
    if (event.detail?.cart) adopt(event.detail.cart);
  });
  window.psGift = { settle };
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))))) mount();
  }).observe(document.body, { childList: true, subtree: true });
  mount();
})();
